import { cloneDeep } from 'lodash'
import Vue from 'vue'
import $store from '@/vuex/store'
import config from '@/configurations/config-environment'
import { BaseService } from '../baseService'
import ExportProjectFieldUpdate from '@/components/export/class/ExportProjectFieldUpdate'
import JSZip from 'jszip'

const dataws = config.apis.dataws
export class ExportService extends BaseService {
  async setExportProjects(exportProjects) {
    for (const project of exportProjects) {
      await this.setExportFiles(project.PrID, project.selectedExportConfig, project.conversionContext)
    }
  }

  async setExportFiles(PrID, selectedExportConfig, conversionContext) {
    const authorisation = this.getAuthorizationObject()
    const languageCode = this.getLangCodeLongFromShort(authorisation.Language)
    selectedExportConfig = selectedExportConfig || {}
    const useCompression = selectedExportConfig.useCompression || false

    // The 'isCorrection' field is added incorrectly to the conversionContext by
    // the export screen, but the export screen also uses it for its own display
    // logic, so we can't just remove it there. Instead, we have to remove it here.
    conversionContext = cloneDeep(conversionContext || {})
    delete conversionContext.isCorrection

    const data = {
      Authorisation: authorisation,
      WebserviceVersion: '1.0',
      LanguageCode: languageCode,
      UseCompression: useCompression,
      TypeOfExport: selectedExportConfig.exportServerIdentifier,
      Parameters: [
        {
          ParameterType: 'ProjectID',
          FloatValue: PrID,
          StringValue: PrID
        },
        {
          ParameterType: 'ConversionContext',
          StringValue: JSON.stringify(conversionContext || {})
        }
      ],
      WithFilteringOnFieldExport: true,
      LogFormat: 'JSON',
      UseZipStream: false
    }
    return Vue.http
      .post(`${dataws}ITWImportExportRestService_V1_0/export`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!response) return
        return this.saveExportProject(response.data, PrID)
      })
      .catch((error) => {
        return console.error(error)
      })
  }

  saveExportProject(response, PrID) {
    const ExportResponseFieldValue = new ExportProjectFieldUpdate('exportResponse', response, PrID)
    $store.commit('updateExportProjectBindingKey', ExportResponseFieldValue)
  }

  downloadExportFiles(response) {
    response.forEach(project => {
      if (project.exportResponse.Files.length === 0) return
      this.downloadFile(project, project.selectedExportConfig, project.PrCode)
    })
  }

  async downloadFile(projectFromResponse, selectedExportConfig, PrCode) {
    const file = projectFromResponse.exportResponse.Files[0]
    const conversionContext = projectFromResponse.conversionContext

    const fileExtension = selectedExportConfig.extension
    const { content, type } = this.decodeFileContent(file.FileContent, fileExtension)
    const fileName = this.changeFileName(PrCode, selectedExportConfig)
    let blob

    switch (conversionContext?.broQualityRegime) {
      case 'IMBRO':
      case 'IMBRO/A':
        const log = projectFromResponse.exportResponse.ExportLog
        const zip = new JSZip()
        zip.file(fileName + '.xml', content)
        zip.file('log.json', log)
        const zipFile = await zip.generateAsync({ type: 'uint8array' })
        blob = new Blob([zipFile], { type: 'application/zip' })
        break

      default:
        blob = new Blob([content], { type })
        break
    }

    this.downloadFileFromTemporaryAnchor(fileName, blob)
  }

  downloadFileFromTemporaryAnchor(fileName, fileContent) {
    const link = document.createElement('a')
    link.href = URL.createObjectURL(fileContent)
    link.download = fileName

    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  decodeFileContent(fileContent, fileExtension) {
    switch (fileExtension) {
      case 'zip':
      case 'ti':
        const decodedContent = atob(fileContent)
        const blobContent = new Uint8Array([...decodedContent].map(char => char.charCodeAt(0)))
        return { content: blobContent, type: 'application/zip' }
      default:
        return { content: fileContent, type: 'text/xml' }
    }
  }

  changeFileName(PrCode, selectedExportConfig) {
    return selectedExportConfig.extension
      ? `${PrCode.replace(/[-.,_{}()\\/:*?"<> ]/g, '')}-${selectedExportConfig.exportTypeName.replace(/[-.,{}()\\/:*?"<> ]/g, '')}.${selectedExportConfig.extension}`
      : `${PrCode.replace(/[-.,_{}()\\/:*?"<> ]/g, '')}-${selectedExportConfig.exportTypeName.replace(/[-.,{}()\\/:*?"<> ]/g, '')}`
  }
}
